import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { NumberParam, type QueryParamConfigMap } from 'serialize-query-params';

import {
  useGetCurrentUser,
  useGetUserOrganizations,
  useGetUserRoleAssignmentsByLocationInfinite
} from '@/api/generated';
import DataGrid from '@/components/DataGrid/DataGrid';
import Field from '@/components/fields/Field';
import { useIsMidotUser } from '@/hooks/permissions/useIsMidotUser';
import { usePlatformPermissions } from '@/hooks/permissions/usePlatformPermissions';
import { DEFAULT_PAGINATION, DEFAULT_SORTING, useEntityList } from '@/hooks/useEntityList';
import { ReadonlyProvider, useReadonly } from '@/hooks/useReadonly';
import { borders } from '@/theme/constants.ts';
import typography from '@/theme/typography';
import OrganizationDropdownSelect from '@/views/fields/OrganizationDropdownSelect/OrganizationDropdownSelect.tsx';
import RoleDropdownSelect from '@/views/fields/RoleDropdownSelect/RoleDropdownSelect';

import { clientUserAccessRolesColumns } from './clientUserAccessRolesColumns.tsx';

const MAX_WIDTH = 1000;

const DEFAULT_VALUES = {
  search: '',
  ...DEFAULT_SORTING,
  ...DEFAULT_PAGINATION
};

const URL_CONFIG_MAP = {
  size: NumberParam
} satisfies QueryParamConfigMap;

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .MuiTableContainer-root': {
    boxShadow: 'none',
    border: 'none',
    borderRadius: 0
  },
  '& .MuiTable-root': {
    border: 'none'
  },
  '& .MuiTableHead-root': {
    boxShadow: 'none',
    '& .MuiTableCell-root': {
      borderTop: 'none',
      borderLeft: borders.default,
      borderRight: borders.default,
      borderBottom: borders.default,
      borderColor: theme.palette.grey[300]
    },
    '& .MuiTableCell-root:first-of-type': {
      borderLeft: 'none'
    },
    '& .MuiTableCell-root:last-of-type': {
      borderRight: 'none'
    }
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '& .MuiTableCell-root': {
        borderTop: 'none',
        borderLeft: borders.default,
        borderRight: borders.default,
        borderBottom: borders.default,
        borderColor: theme.palette.grey[300]
      },
      '& .MuiTableCell-root:first-of-type': {
        borderLeft: 'none'
      },
      '& .MuiTableCell-root:last-of-type': {
        borderRight: 'none'
      }
    }
  }
}));

const UserSettingsAccessRoles: React.FC = () => {
  const { t } = useTranslation(['users', 'settings', 'common']);
  const { watch } = useFormContext();

  const isMidotEmployee = watch('isMidotEmployee');

  return (
    <Stack
      width="100%"
      sx={{
        '& .MuiFormLabel-root': { textAlign: 'start' },
        '& .MuiFormControl-root': { pb: 0 },
        '& .DropdownSelect-expandMoreIcon': { color: 'text.primary' }
      }}
    >
      <Stack direction="row" justifyContent="space-between" width="100%" p={3}>
        <Typography sx={{ ...typography.body1, fontWeight: typography.fontWeightMedium }}>
          {t('users.sections.roles')}
        </Typography>
      </Stack>

      {isMidotEmployee ? <MidotUserSettingsAccessRolesContent /> : <ClientUserSettingsAccessRolesContent />}
    </Stack>
  );
};

const MidotUserSettingsAccessRolesContent: React.FC = () => {
  const { t } = useTranslation(['users', 'settings', 'common']);
  const { spacing } = useTheme();
  const { isReadonly } = useReadonly();
  const { userId } = useParams<{ userId: string }>();
  const { data: currentUser } = useGetCurrentUser();
  const { isMidotUser } = useIsMidotUser();
  const isOwnProfile = currentUser?.id === userId;

  const { value: { canAssignMidotRoles } = {} } = usePlatformPermissions();

  return (
    <ReadonlyProvider readonly={!canAssignMidotRoles || isReadonly || (!isMidotUser && isOwnProfile)}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          mx: 3,
          gridColumnGap: spacing(2),
          gridRowGap: spacing(3),
          maxWidth: MAX_WIDTH,
          '& .MuiInputBase-root': { minHeight: spacing(5) },
          '& .MuiFormControl-root': { px: 0, pt: 0 },
          '& .DropdownSelect-control': { minHeight: spacing(5) }
        }}
      >
        <Field
          component={RoleDropdownSelect}
          name="midotRoles"
          label={t('users.fields.roles')}
          withFormControl
          markAsRequired
          innerProps={{
            isMulti: true,
            usePortal: true
          }}
        />
      </Box>
    </ReadonlyProvider>
  );
};

const ClientUserSettingsAccessRolesContent: React.FC = () => {
  const { t } = useTranslation(['users', 'settings', 'common']);
  const { watch } = useFormContext();
  const { userId } = useParams<{ userId: string }>();
  const [queryBody, setQueryBody] = useState<unknown>(null);

  const isMidotEmployee = watch('isMidotEmployee');

  const {
    data: roleAssignmentsData,
    isError,
    isLoading: isLoadingRoleAssignments,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetUserRoleAssignmentsByLocationInfinite(userId!, queryBody ?? {}, {
    query: { enabled: !!queryBody && !!userId && !isMidotEmployee }
  });

  const isLoading = isLoadingRoleAssignments || !queryBody || !userId || isMidotEmployee;

  const roleAssignmentsList = useMemo(
    () => roleAssignmentsData?.pages?.flatMap(page => page?.userLocationRoleAssignments || []) || [],
    [roleAssignmentsData]
  );

  const handleFetchNextPage = useCallback(() => {
    if (isError || isFetchingNextPage || !hasNextPage || !roleAssignmentsData?.pages?.length) return;
    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isError, isFetchingNextPage, roleAssignmentsData?.pages?.length]);

  const { sorting, handleChangeSort } = useEntityList(DEFAULT_VALUES, URL_CONFIG_MAP, setQueryBody, undefined, 300);

  const columns = useMemo(() => clientUserAccessRolesColumns(t), [t]);

  const { data: organizationsData } = useGetUserOrganizations(
    userId!,
    { adminOnly: true },
    {
      query: {
        enabled: !!userId && !isMidotEmployee
      }
    }
  );

  const organizationsList = useMemo(
    () =>
      organizationsData?.organizations
        ?.map(organization => organization.slug)
        .filter((slug): slug is string => Boolean(slug)) || [],
    [organizationsData]
  );

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" px={3}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography
            variant="body3"
            sx={{
              fontWeight: typography.fontWeightMedium,
              color: 'grey.700'
            }}
          >
            {t('users.fields.organizationAdmin')}
          </Typography>
          <OrganizationDropdownSelect
            value={organizationsList}
            name="organizations"
            isMulti
            isDisabled
            sx={{
              '& .DropdownSelect-clearIcon': {
                display: 'none'
              }
            }}
          />
        </Stack>
        {/* // TODO: Restore once we have the modal implemented
          <Button
            variant="outlined"
            onClick={handleAssignRole}
            sx={{
              ...typography.body2,
              fontWeight: typography.fontWeightMedium,
              py: 0.5,
              paddingInlineStart: 1,
              paddingInlineEnd: 1.5,
              '& .MuiButton-icon': {
                marginInlineStart: 0,
                marginInlineEnd: 1
              },
              '& .PlusIcon-root': { width: spacing(2), height: spacing(2) }
            }}
            startIcon={<PlusIcon className="PlusIcon-root" />}
          >
            {t('users.actions.assignRoleToUser')}
          </Button>
          */}
      </Stack>
      <Box sx={{ px: 5, flexGrow: 1, maxWidth: MAX_WIDTH }}>
        <StyledDataGrid
          data={roleAssignmentsList}
          columns={columns}
          isLoading={isLoading}
          isLoadingNextPage={isFetchingNextPage}
          onSort={handleChangeSort}
          hasNextPage={hasNextPage}
          sorting={sorting}
          resizeWatchSelector=".Filters-root"
          hasError={isError && !roleAssignmentsList.length}
          onLoadNextPage={handleFetchNextPage}
          hasStickyRow={false}
        />
      </Box>
    </>
  );
};

export default UserSettingsAccessRoles;
